import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { usePostHog } from "@posthog/react";
import { Cookie } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { CONSENT_KEY as STORAGE_KEY } from "@/posthog";

export default function ConsentBanner() {
  const posthog = usePostHog();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Opt state is already reconciled in posthog.js at init; here we only decide whether to
    // show the notice (i.e. the visitor hasn't made a choice yet).
    if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
  }, []);

  const accept = () => {
    localStorage.setItem(STORAGE_KEY, "granted");
    posthog?.opt_in_capturing();
    setVisible(false);
  };

  const decline = () => {
    localStorage.setItem(STORAGE_KEY, "denied");
    posthog?.opt_out_capturing();
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-6 sm:max-w-md z-[60]"
          data-testid="consent-banner"
          role="dialog"
          aria-label="Analytics consent"
        >
          <div className="bg-[#fdfdfb] border border-[#14140f]/12 shadow-xl rounded-2xl p-5">
            <div className="flex items-start gap-3">
              <span className="shrink-0 w-9 h-9 rounded-lg bg-[#f5f4f0] flex items-center justify-center">
                <Cookie className="w-5 h-5 text-[#c23c28]" />
              </span>
              <div className="text-sm text-[#4e6073] leading-relaxed">
                We use privacy-friendly analytics — including the searches you run — to improve
                results and the wisdom map. No name or email is tied to your searches. You can
                opt out anytime.{" "}
                <Link
                  to="/privacy"
                  data-testid="consent-privacy-link"
                  className="text-[#c23c28] underline underline-offset-2 hover:text-[#c23c28]"
                >
                  Learn more
                </Link>
                .
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-4">
              <Button
                variant="ghost"
                onClick={decline}
                data-testid="consent-decline-btn"
                className="rounded-full text-[#14140f]/70 hover:text-[#14140f] hover:bg-[#f5f4f0]"
              >
                Opt out
              </Button>
              <Button
                onClick={accept}
                data-testid="consent-accept-btn"
                className="rounded-full bg-[#ff7360] hover:bg-[#e85a46] text-[#14140f] px-5"
              >
                Got it
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
